import { useCallback, useMemo, useState } from "react";
import { Ban, Info } from "lucide-react";

import EmptyState from "assets/images/medications-empty-state.svg";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Pill } from "components/ui/Pill";
import { PILL_COLORS } from "components/ui/Pill/pillColors";
import { Textarea } from "components/ui/Textarea";
import { formatDate } from "utils/dateFormatter";

import { FREQUENCY_LABEL, ROUTE_LABEL } from "./constants";
import styles from "./DrugsTable.module.css";

const FILTERS_CONFIG = [
  {
    id: "showStopped",
    label: "",
    type: "single",
    options: [{ value: "true", label: "Show stopped" }],
  },
];

const ROWS_OPTIONS = [10, 20, 50, 100, "All"];

export const DrugsTable = ({
  items,
  isLoading,
  isError,
  errorLabel,
  onRetry,
  emptyTitle,
  selection,
  deleteDrug,
  currentPage,
  setCurrentPage,
  searchQuery,
  setSearchQuery,
  totalRecords,
  rows = "All",
  setRows,
  tradeNameWidth = "340px",
}) => {
  const [itemToStop, setItemToStop] = useState(null);
  const [isStopping, setIsStopping] = useState(false);
  const [stopReason, setStopReason] = useState("");
  const [stopReasonItem, setStopReasonItem] = useState(null);
  const [filters, setFilters] = useState({});

  const filteredItems = useMemo(
    () =>
      filters.showStopped === "true"
        ? items
        : items.filter((item) => !item.stopMedication),
    [items, filters.showStopped]
  );

  const handleOpenStopDialog = useCallback((item) => {
    setItemToStop(item);
    setStopReason("");
  }, []);

  const handleCloseStopDialog = useCallback(() => {
    setItemToStop(null);
    setStopReason("");
  }, []);

  const handleConfirmStop = useCallback(async () => {
    if (!itemToStop || !stopReason.trim()) return;
    setIsStopping(true);
    try {
      await deleteDrug(itemToStop.patientMedicationId || itemToStop.id, stopReason.trim());
      setItemToStop(null);
      setStopReason("");
    } finally {
      setIsStopping(false);
    }
  }, [itemToStop, stopReason, deleteDrug]);

  const columns = useMemo(
    () => [
      {
        id: "tradeName",
        header: "Drug Name",
        width: tradeNameWidth,
        noDim: true,
        // Active meds render as a plain string so the DataTable cell handles the
        // ellipsis truncation and shows the overflow tooltip with the full name.
        // Stopped meds keep the pill + dimmed name (tooltip via getTooltipText).
        render: (item) => {
          const name = item.tradeName || "-";

          if (!item.stopMedication) return name;

          return (
            <span className={styles.drugNameRow}>
              <Pill {...PILL_COLORS.deceased}>Stopped</Pill>
              <span className={styles.drugNameDimmed}>{name}</span>
            </span>
          );
        },
        getTooltipText: (item) => item.tradeName || "-",
      },
      {
        id: "dosage",
        header: "Dosage",
        width: "100px",
        render: (item) => item.dosage || "-",
      },
      {
        id: "frequency",
        header: "Frequency",
        width: "140px",
        render: (item) =>
          item.frequency ? (
            <Pill {...PILL_COLORS.method}>
              {FREQUENCY_LABEL(item.frequency)}
            </Pill>
          ) : (
            "-"
          ),
      },
      {
        id: "route",
        header: "Route",
        width: "110px",
        render: (item) =>
          item.route ? (
            <Pill {...PILL_COLORS.site}>{ROUTE_LABEL(item.route)}</Pill>
          ) : (
            "-"
          ),
      },
      {
        id: "timesPrescribed",
        header: "Repeats",
        width: "70px",
        render: (item) => item.timesPrescribed ?? "-",
      },
      {
        id: "startDate",
        header: "Start Date",
        width: "minmax(95px, 1fr)",
        render: (item) => formatDate(item.startDate) || "-",
      },
    ],
    [tradeNameWidth]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Stop medication",
        icon: Ban,
        primary: true,
        visibleWhen: (item) => !item.stopMedication,
        onClick: (item) => handleOpenStopDialog(item),
      },
      {
        label: "View stop reason",
        icon: Info,
        primary: true,
        visibleWhen: (item) => !!item.stopMedication,
        onClick: (item) => setStopReasonItem(item),
      },
    ],
    [handleOpenStopDialog]
  );

  return (
    <>
    <DataTable
      columns={columns}
      data={filteredItems}
      getRowId={(row) => row.id}
      isLoading={isLoading}
      isError={isError}
      errorLabel={errorLabel}
      onRetry={onRetry}
      emptyIllustration={searchQuery ? undefined : EmptyState}
      emptyTitle={emptyTitle || "No medications"}
      emptyText={
        searchQuery
          ? "Try adjusting your search or filters to find what you're looking for."
          : "Once the patient receives a prescription, it will appear here."
      }
      search={{
        value: searchQuery,
        onChange: (v) => {
          setSearchQuery(v);
          setCurrentPage(1);
        },
        placeholder: "Search medications...",
      }}
      selectable={!!selection}
      selection={selection}
      rowActions={rowActions}
      getRowClassName={(row) => (row.stopMedication ? "disabled" : undefined)}
      filtersConfig={FILTERS_CONFIG}
      filters={{
        value: filters,
        onChange: (groupId, value) =>
          setFilters((prev) => ({ ...prev, [groupId]: value })),
      }}
      currentPage={currentPage}
      rowsPerPage={rows === "All" ? totalRecords || 9999 : rows}
      rowsValue={rows}
      onRowsChange={
        setRows
          ? (val) => {
              setRows(val);
              setCurrentPage(1);
            }
          : undefined
      }
      rowsOptions={ROWS_OPTIONS}
      totalCount={rows === "All" ? undefined : totalRecords}
      onPageChange={setCurrentPage}
    />

    <Dialog
      open={!!stopReasonItem}
      onClose={() => setStopReasonItem(null)}
      title="Stop reason"
      width={500}
    >
      <p className={styles.stopDialogMessage}>{stopReasonItem?.tradeName}</p>
      <p>{stopReasonItem?.stopReason || "No reason provided."}</p>
      <div className={styles.stopDialogActions}>
        <Button variant="outline" size="medium" onClick={() => setStopReasonItem(null)}>
          Close
        </Button>
      </div>
    </Dialog>

    <Dialog
      open={!!itemToStop}
      onClose={handleCloseStopDialog}
      title="Stop medication"
      width={500}
    >
      <p className={styles.stopDialogMessage}>
        Please provide a reason for stopping this medication.
      </p>
      <Textarea
        placeholder="Enter stop reason..."
        value={stopReason}
        onChange={(e) => setStopReason(e.target.value)}
        autoFocus
      />
      <div className={styles.stopDialogActions}>
        <Button variant="outline" size="medium" onClick={handleCloseStopDialog} disabled={isStopping}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="medium"
          onClick={handleConfirmStop}
          disabled={isStopping || !stopReason.trim()}
        >
          {isStopping ? "Stopping..." : "Stop medication"}
        </Button>
      </div>
    </Dialog>
  </>
  );
};
